<template>
    <div class="relative w-full h-64 md:h-80">
        <div class="absolute top-0 left-0 w-full h-full">
            <canvas ref="canvasRef"></canvas>
        </div>
        <SkeletonLoader v-if="isLoading" class="absolute top-0 left-0 w-full h-full rounded-lgr" />
        <div v-if="!isLoading && !data?.length" class="absolute top-0 left-0 w-full h-full p-2 flex items-center justify-center text-ink-muted">No data available.</div>
    </div>
</template>

<script setup lang="ts">
import "chartjs-adapter-luxon"; // Needed for TimeScale
import { cssColor } from "~vue/ChatHome/workiqTrendColors";
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { Chart, LinearScale, LineController, LineElement, PointElement, TimeScale } from "chart.js";
import { onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";

import type { ImpactResponse } from "./types";

const canvasRef = useTemplateRef("canvasRef");
const chartRef = ref<Chart | null>(null);

const {
    data = null,
    isLoading = false,
    responsive = true,
} = defineProps<{
    data?: ImpactResponse["history"] | null;
    isLoading?: boolean;
    responsive?: boolean; // Used only in testing to ensure we catch missing registrations
}>();

const updateChart = (newIsLoading: boolean, newData: ImpactResponse["history"] | null) => {
    if (!canvasRef.value) {
        return;
    }
    if (chartRef.value) {
        chartRef.value.destroy();
    }
    if (newIsLoading || !newData?.length) {
        return;
    }

    chartRef.value = new Chart(canvasRef.value, {
        type: "line",
        data: {
            labels: newData ? newData.map((d) => d.date) : [],
            datasets: [
                {
                    label: "Count",
                    data: newData ? newData.map((d) => d.count) : [],
                    tension: 0.35,
                    pointRadius: 4,
                    borderColor: "#8489CF",
                    backgroundColor: "#8489CF",
                    borderWidth: 3,
                },
            ],
        },
        options: {
            animation: false,
            responsive,
            maintainAspectRatio: false,
            scales: {
                x: {
                    type: "time",
                    time: {
                        unit: "month",
                        displayFormats: {
                            month: "MMM `yy",
                        },
                    },
                    ticks: {
                        color: cssColor("--color-ink-muted", "#6B7280"),
                        font: {
                            family: "Inter, sans-serif",
                            size: 14,
                        },
                    },
                },
                y: {
                    beginAtZero: true,
                    grace: "15%",
                    ticks: {
                        color: cssColor("--color-ink-muted", "#6B7280"),
                        font: {
                            family: "Inter, sans-serif",
                            size: 14,
                        },
                    },
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                },
                datalabels: {
                    display: false,
                },
            },
        },
    });
};

watch([() => isLoading, () => data], ([newIsLoading, newData]) => updateChart(newIsLoading, newData));

onMounted(() => {
    Chart.register(LineController, TimeScale, LinearScale, PointElement, LineElement);
    updateChart(isLoading, data);
});

onUnmounted(() => {
    chartRef.value?.destroy();
});
</script>
